import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Portal } from '@/components/ui/portal';
import { Select } from '@/components/ui/select';
import { useShouldPresentAsSheet } from '@/lib/useMediaQuery';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useAnchoredPosition } from '@/lib/useAnchoredPosition';
import { partyLabel, type PartyValue,
  MIN_CHILD_AGE,
  MAX_CHILD_AGE,
} from '@/lib/party';
import { m } from '@/paraglide/messages';

// Ages 2–17 are children (priced, tourist-tax banded); under 2 are infants
// (no capacity, no age asked). See lib/party.ts and the task-12 brief.
// Single source of truth in lib/party.ts — the URL normalizer applies the
// same bounds, and a second copy here would be the one that drifts.
const CHILD_AGE_MIN = MIN_CHILD_AGE;
const CHILD_AGE_MAX = MAX_CHILD_AGE;
const CHILD_AGES = Array.from(
  { length: CHILD_AGE_MAX - CHILD_AGE_MIN + 1 },
  (_, i) => CHILD_AGE_MIN + i,
);

// childrenAllowed/infantsAllowed/maxInfants are optional (unlike
// occupancyMax): the guest booking API's PublicApartment payload does not
// carry these three house-rule fields yet (they exist on the ops-side
// Apartment schema and the internal domain projection, but were never wired
// onto the public wire DTO — see BookingWidget.tsx's own comment). A caller
// that doesn't have real values MUST leave them absent rather than guess —
// absent reads as "unconstrained" below, same as the picker already treats
// a wholly-absent `limits` prop.
export interface GuestPickerLimits {
  occupancyMax: number;
  childrenAllowed?: boolean;
  infantsAllowed?: boolean;
  maxInfants?: number;
}

export interface GuestPickerProps {
  value: PartyValue;
  onChange: (next: PartyValue) => void;
  // Absent on the home page and search results, where no apartment (and so
  // no capacity) is known yet — there, only "at least one adult" applies.
  limits?: GuestPickerLimits;
}

export function GuestPicker({ value, onChange, limits }: GuestPickerProps) {
  const [open, setOpen] = useState(false);
  // Narrow OR short (OH-131) — shares the gate DateRangePicker uses, see
  // useShouldPresentAsSheet's own comment for why this is a separate hook
  // from useIsMobile.
  const presentAsSheet = useShouldPresentAsSheet();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Same open/close idiom as DateRangePicker (Escape closes, an outside
  // click closes, focus returns to the trigger either way) — deliberately
  // NOT a second implementation of the same behaviour. Two popovers in the
  // same search bar that behave differently reads as a bug even when
  // neither one, in isolation, is wrong.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    function onPointerDown(e: MouseEvent) {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  useFocusTrap(panelRef, open);

  // Only the desktop popover needs a computed position — the sheet is
  // pinned to the viewport edge by its own CSS, same reasoning as
  // DateRangePicker.
  const anchorPosition = useAnchoredPosition(triggerRef, panelRef, open && !presentAsSheet);

  // True once the panel is actually visible — see DateRangePicker.tsx's
  // identical `measured` for the full reasoning (same idiom, deliberately
  // not diverged).
  const measured = presentAsSheet || anchorPosition !== null;

  // Move focus into the panel so the next Tab lands inside it, not past it.
  // A SEPARATE effect from the listener-registration one above — see
  // DateRangePicker.tsx's identical split for why: an effect keyed on
  // `[open]` alone fires while the popover is still `visibility: hidden`
  // (React flushes a commit's pending passive effects before the
  // synchronous re-render `useAnchoredPosition`'s measurement triggers),
  // making `.focus()` a no-op. `measured`, not `anchorPosition` itself, is
  // the dependency: `anchorPosition` also changes on every scroll/resize
  // recompute, which would otherwise re-run this effect and yank focus away
  // on every scroll tick.
  //
  // preventScroll: same reason as DateRangePicker's identical call — the
  // browser's default "scroll the focused element into view" walks up to
  // the nearest scrollable ancestor, which on the home page is the hero
  // section, not this panel (see DateRangePicker.tsx for the measurements).
  useEffect(() => {
    if (!open || !measured) return;
    panelRef.current?.focus({ preventScroll: true });
  }, [open, measured]);

  // occupancy_max counts adults + children together; infants are capped
  // separately (they don't consume capacity) — CLAUDE.md / brief §4.
  const occupied = value.adults + value.childrenAges.length;
  const canAddAdult = limits ? occupied < limits.occupancyMax : true;
  const canRemoveAdult = value.adults > 1;
  const canAddChild = limits
    ? (limits.childrenAllowed ?? true) && occupied < limits.occupancyMax
    : true;
  const canRemoveChild = value.childrenAges.length > 0;
  const canAddInfant = limits
    ? (limits.infantsAllowed ?? true) && value.infants < (limits.maxInfants ?? Infinity)
    : true;
  const canRemoveInfant = value.infants > 0;

  function addChild() {
    // A new child ALWAYS starts with no age — never default it, an
    // unanswered question must stay unanswered (brief §2).
    onChange({ ...value, childrenAges: [...value.childrenAges, null] });
  }

  function removeChild() {
    // Removes the LAST slot, answered or not.
    onChange({ ...value, childrenAges: value.childrenAges.slice(0, -1) });
  }

  function setChildAge(index: number, age: number | null) {
    const childrenAges = value.childrenAges.slice();
    childrenAges[index] = age;
    onChange({ ...value, childrenAges });
  }

  const steppers = (
    <>
      <CounterRow
        label={m.guest_picker_adults_label()}
        hint={m.guest_picker_adults_hint()}
        value={value.adults}
        onIncrement={() => onChange({ ...value, adults: value.adults + 1 })}
        onDecrement={() => onChange({ ...value, adults: value.adults - 1 })}
        canIncrement={canAddAdult}
        canDecrement={canRemoveAdult}
        incrementLabel={m.guest_picker_add_adult()}
        decrementLabel={m.guest_picker_remove_adult()}
      />
      <CounterRow
        label={m.guest_picker_children_label()}
        hint={m.guest_picker_children_hint()}
        value={value.childrenAges.length}
        onIncrement={addChild}
        onDecrement={removeChild}
        canIncrement={canAddChild}
        canDecrement={canRemoveChild}
        incrementLabel={m.guest_picker_add_child()}
        decrementLabel={m.guest_picker_remove_child()}
      />
      {value.childrenAges.length > 0 ? (
        <div className="mb-2 flex flex-col gap-2 pl-1">
          {value.childrenAges.map((age, index) => (
            // A child slot's identity IS its position: addChild/removeChild
            // only ever append/pop the LAST one, so index stays stable
            // across renders (no reordering, no mid-list insert/delete).
            <Select
              key={index}
              label={m.guest_picker_child_label({ index: index + 1 })}
              value={age === null ? '' : String(age)}
              onChange={(e) =>
                setChildAge(index, e.target.value === '' ? null : Number(e.target.value))
              }
              placeholder={m.guest_picker_child_age_placeholder()}
              options={CHILD_AGES.map((a) => ({ value: String(a), label: String(a) }))}
            />
          ))}
        </div>
      ) : null}
      <CounterRow
        label={m.guest_picker_infants_label()}
        hint={m.guest_picker_infants_hint()}
        value={value.infants}
        onIncrement={() => onChange({ ...value, infants: value.infants + 1 })}
        onDecrement={() => onChange({ ...value, infants: value.infants - 1 })}
        canIncrement={canAddInfant}
        canDecrement={canRemoveInfant}
        incrementLabel={m.guest_picker_add_infant()}
        decrementLabel={m.guest_picker_remove_infant()}
      />
    </>
  );

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-haspopup="dialog"
      className="w-full rounded-2xl px-4 py-2 text-left sm:rounded-full"
    >
      <span className="block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
        {m.guest_picker_label()}
      </span>
      <span className="mt-0.5 block truncate text-sm font-semibold text-[var(--text-body)]">
        {partyLabel(value)}
      </span>
    </button>
  );

  // Narrow OR short, same as DateRangePicker: one gesture on both surfaces —
  // tap the field, get a bottom sheet.
  if (presentAsSheet) {
    return (
      <div>
        {trigger}
        {open ? (
          <Portal>
            <div className="fixed inset-0 z-[1000] flex flex-col justify-end bg-[rgba(15,23,42,0.55)]">
              <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={m.guest_picker_label()}
                tabIndex={-1}
                // dvh, not vh: on iOS Safari `vh` measures the LARGEST
                // viewport, so an 85vh sheet extends under the collapsing
                // browser chrome and can put the sticky Done button out of
                // reach. Not reproducible on the Chrome/macOS harness this
                // was found on — fixed on the owner's call (OH-130 D6).
                className="max-h-[85dvh] overflow-y-auto rounded-t-[var(--radius-2xl)] bg-card p-4"
              >
                {steppers}
                <Button
                  variant="primary"
                  size="lg"
                  className="sticky bottom-0 mt-3 w-full"
                  onClick={() => {
                    // Restore focus to the trigger, matching Escape/outside
                    // click (see DateRangePicker.tsx's identical Done
                    // button for the fuller reasoning).
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  {m.picker_done()}
                </Button>
              </div>
            </div>
          </Portal>
        ) : null}
      </div>
    );
  }

  return (
    // `relative` is vestigial: the portaled panel below is `position:
    // fixed` (via useAnchoredPosition), not `absolute`, so nothing here
    // actually anchors it — harmless only as long as that stays true.
    <div className="relative">
      {trigger}
      {open ? (
        <Portal>
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={m.guest_picker_label()}
            tabIndex={-1}
            // Same reasoning as DateRangePicker: on the apartment page this
            // panel used to open downwards inside a sticky sidebar taller
            // than the viewport — 26 of its 190px were visible and no amount
            // of scrolling reached the rest (OH-130 §1 D4).
            style={{
              position: 'fixed',
              top: anchorPosition?.top ?? 0,
              left: anchorPosition?.left ?? 0,
              visibility: anchorPosition ? 'visible' : 'hidden',
            }}
            className="z-[1000] w-[320px] max-w-[90vw] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4 shadow-[var(--shadow-lg)]"
          >
            {steppers}
          </div>
        </Portal>
      ) : null}
    </div>
  );
}

function CounterRow({
  label,
  hint,
  value,
  onIncrement,
  onDecrement,
  canIncrement,
  canDecrement,
  incrementLabel,
  decrementLabel,
}: {
  label: ReactNode;
  hint: ReactNode;
  value: number;
  onIncrement: () => void;
  onDecrement: () => void;
  canIncrement: boolean;
  canDecrement: boolean;
  incrementLabel: string;
  decrementLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div>
        <div className="text-sm font-semibold text-[var(--text-heading)]">{label}</div>
        <div className="text-xs text-muted">{hint}</div>
      </div>
      <div className="flex items-center gap-3">
        <IconButton
          label={decrementLabel}
          onClick={onDecrement}
          disabled={!canDecrement}
          size="sm"
          round
          variant="outline"
        >
          <Minus className="h-4 w-4" aria-hidden="true" />
        </IconButton>
        <span className="w-4 text-center text-sm font-semibold tabular-nums">{value}</span>
        <IconButton
          label={incrementLabel}
          onClick={onIncrement}
          disabled={!canIncrement}
          size="sm"
          round
          variant="outline"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </div>
    </div>
  );
}
